/** @jsxImportSource @emotion/react */

import { useState, useRef, useEffect, useCallback } from "react";

import { accessAPI, formatFecha, Sidebar, TablaFiltrable } from "../../../shared";

import { Column } from "primereact/column";
import { Image } from "primereact/image";
import { Skeleton } from "primereact/skeleton";

// import { Tooltip } from "primereact/tooltip";

import { Titulo } from "../../../shared";
import { Formulario } from "../../../shared";

import { Toast } from "primereact/toast";
import { Boton } from "../../../shared";

export default function ProductoEnActividad({
  producto,
  pasoId,
  onProductosActualizados,
  modoLectura,
}) {
  const [loaderSubmit, setLoaderSubmit] = useState(false);
  const [camposEnProducto, setCamposEnProducto] = useState(
    producto.producto_x_productocampospropios
  );
  const [campoSeleccionado, setCampoSeleccionado] = useState();
  const [formularioConfig, setFormularioConfig] = useState();
  const [sideBar, setSideBar] = useState(false);
  const [archivosComentarios, setArchivosComentarios] = useState({});
  // const [verificacionSeleccionada, setVerificacionSeleccionada] = useState();

  const toast = useRef(null);

  useEffect(() => {
    setCamposEnProducto(producto.producto_x_productocampospropios);
  }, [producto]);

  // Cambios pendientes de emisión del producto, indexados por el id del campo
  // (producto_x_productocampospropios). Vienen del GET de la actividad y se
  // refrescan con cada respuesta de validarcampos.
  const cambiosPorCampo = {};
  for (const cambio of producto.cambioproductocertificado || []) {
    if (!cambio.bajaproducto && cambio.productocampopropioid) {
      cambiosPorCampo[cambio.productocampopropioid] = cambio;
    }
  }
  const hayCambiosPendientes = Object.keys(cambiosPorCampo).length > 0;
  const cambioDelCampoSeleccionado = campoSeleccionado
    ? cambiosPorCampo[campoSeleccionado.id]
    : null;

  const cargarArchivoComentario = useCallback(
    (comentario) => {
      if (archivosComentarios[comentario.id]) {
        return; // Ya está cargado
      }

      accessAPI(
        "GET",
        `colab/actividad/comentariocampo/${comentario.id}/imagen`,
        null,
        (blob) => {
          const url = URL.createObjectURL(blob);
          setArchivosComentarios((prev) => ({
            ...prev,
            [comentario.id]: {
              url,
              esImagen: blob.type?.startsWith("image/"),
              nombreDescarga: comentario.archivo?.nombredescarga,
            },
          }));
        },
        (error) => {
          console.error("Error cargando archivo:", error);
        },
        null,
        true // blob
      );
    },
    [archivosComentarios]
  );

  useEffect(() => {
    if (campoSeleccionado?.comentarios) {
      campoSeleccionado.comentarios.forEach((comentario) => {
        if (comentario.archivo) {
          cargarArchivoComentario(comentario);
        }
      });
    }
  }, [campoSeleccionado, cargarArchivoComentario]);

  useEffect(() => {
    if (campoSeleccionado) {
      // Genera un objeto para formatear la configuración según la API de formulario
      let campoParaConfigurar = {};
      campoParaConfigurar.nombre = "valor";
      campoParaConfigurar.etiqueta = "Editar campo";
      campoParaConfigurar.notas =
        campoSeleccionado.campo?.productocampospropios?.notas;
      campoParaConfigurar.respuesta = campoSeleccionado.campo?.valor;

      // Determina el tipo de campo según el tipo
      const tipoCampo = campoSeleccionado.campo?.productocampospropios?.tipo;
      switch (tipoCampo) {
        case "int":
          campoParaConfigurar.tipoitemformularioid = 4;
          break;
        case "float":
          campoParaConfigurar.tipoitemformularioid = 8;
          break;
        default:
          campoParaConfigurar.tipoitemformularioid = 1;
          break;
      }

      // Si tiene opciones las carga
      if (campoSeleccionado.campo?.productocampospropios?.opciones?.length) {
        campoParaConfigurar.opciones =
          campoSeleccionado.campo.productocampospropios.opciones.map(
            (opcion) => {
              return { value: opcion, etiqueta: opcion };
            }
          );
        campoParaConfigurar.sinOpcionInicial = true;
        campoParaConfigurar.tipoitemformularioid = 5;
      }

      setFormularioConfig({
        secciones: [
          {
            descripcion: `Edite el valor del campo ${campoSeleccionado.nombre} para este producto específico.`,
            campos: [campoParaConfigurar],
          },
          {
            descripcion: `Ingrese comentarios sobre esta validación del campo.`,
            campos: [
              {
                nombre: "comentario",
                tipoitemformularioid: 11,
                etiqueta: "Comentarios",
              },
            ],
          },
          {
            campos: [
              {
                nombre: "aplicarATodos",
                tipoitemformularioid: 6,
                etiqueta: "Aplicar a todos los productos",
                notas:
                  "Marque esta opción para aplicar la misma decisión a este campo en todos los productos del proceso.",
              },
            ],
          },
        ],
        botones: [
          {
            etiqueta: "Rechazar",
            callback: (contenidoCampos) => {
              validarCampo(contenidoCampos, false);
            },
            validar: true,
            confirmacion: "¿Rechazar este campo?",
            rojo: true,
          },
          {
            etiqueta: "Aprobar",
            callback: (contenidoCampos) => {
              validarCampo(contenidoCampos, true);
            },
            validar: true,
            confirmacion: "¿Aprobar este campo?",
            verde: true,
          },
        ],
      });
      setSideBar(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campoSeleccionado]);

  // Cuando se cierra el sidebar, se des-selecciona el campo
  useEffect(() => {
    if (!sideBar) {
      setTimeout(() => setCampoSeleccionado(null), 500);
    }
  }, [sideBar]);

  function validarCampo(contenidoCampos, aprobado) {
    setLoaderSubmit(true);
    // Agrega el booleano de aprobado al objeto de campos
    contenidoCampos.aprobacion = aprobado;
    contenidoCampos.productoId = producto.id;

    accessAPI(
      "PUT",
      `colab/actividad/${pasoId}/validarcampos/${campoSeleccionado.id}`,
      contenidoCampos,
      (respuesta) => {
        setSideBar(false);
        setLoaderSubmit(false);
        // Actualiza los productos con la respuesta del servidor
        if (respuesta.productos && onProductosActualizados) {
          onProductosActualizados(respuesta.productos);
        }
        if (respuesta.cambiosPendientes) {
          // El producto tiene certificado emitido: el nuevo valor no se
          // aplicó directo, quedó pendiente de emisión
          toast.current.show({
            severity: "warn",
            summary: "Cambio pendiente de emisión",
            detail:
              "El producto tiene certificado emitido: el nuevo valor quedó pendiente y se aplicará en la próxima emisión",
            life: 6000,
          });
        } else {
          toast.current.show({
            severity: "success",
            summary: "Éxito",
            detail: "Campo validado correctamente",
            life: 3000,
          });
        }
      },
      (respuesta) => {
        setLoaderSubmit(false);
        setSideBar(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0].msg ||
            "Ocurrió un error, intente nuevamente más tarde",
          sticky: true,
        });
      }
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Campo - {campoSeleccionado?.nombre}</Titulo>

          {/* Cartel de cambio pendiente de emisión para este campo */}
          {cambioDelCampoSeleccionado && (
            <div
              css={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.6em",
                padding: "0.75em 1em",
                marginBottom: "1em",
                borderRadius: "6px",
                border: "1px solid var(--rojo)",
                backgroundColor: "#fff5f5",
              }}
            >
              <i
                className="pi pi-exclamation-triangle"
                css={{ color: "var(--rojo)", marginTop: "0.15em" }}
              ></i>
              <div>
                <strong>Este campo tiene un cambio pendiente de emisión.</strong>
                <div css={{ marginTop: "0.25em" }}>
                  {cambioDelCampoSeleccionado.valoranterior || "(vacío)"} →{" "}
                  <strong>
                    {cambioDelCampoSeleccionado.valornuevo || "(vacío)"}
                  </strong>
                </div>
              </div>
            </div>
          )}

          <Formulario config={formularioConfig} loader={loaderSubmit} />

          <p css={{ marginTop: "2em" }}>Comentarios de pasos anteriores:</p>
          {campoSeleccionado?.comentarios &&
          campoSeleccionado.comentarios.length > 0 ? (
            <div css={{ marginTop: "1em" }}>
              {campoSeleccionado.comentarios.map((comentario) => (
                <div
                  key={comentario.id}
                  css={{
                    marginBottom: "1em",
                    padding: "1em",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: comentario.aprobado
                      ? "#f0f9f0"
                      : "#fff5f5",
                  }}
                >
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "0.5em",
                    }}
                  >
                    <i
                      className={
                        comentario.aprobado ? "pi pi-check" : "pi pi-times"
                      }
                      css={{
                        color: comentario.aprobado
                          ? "var(--verde)"
                          : "var(--rojo)",
                        marginRight: "0.5em",
                      }}
                    ></i>
                    <strong>
                      {comentario.aprobado ? "Aprobado" : "Rechazado"}
                    </strong>
                  </div>
                  {comentario.comentario && (
                    <p css={{ margin: "0.5em 0" }}>{comentario.comentario}</p>
                  )}
                  {comentario.archivo && (
                    <div css={{ margin: "1em 0" }}>
                      {archivosComentarios[comentario.id] ? (
                        archivosComentarios[comentario.id].esImagen ? (
                          <Image
                            src={archivosComentarios[comentario.id].url}
                            alt="Imagen del comentario"
                            width="200"
                            preview
                            imageStyle={{ borderRadius: "4px" }}
                            previewIcon={<i className="pi pi-search"></i>}
                          />
                        ) : (
                          <Boton
                            callback={() => {
                              const archivo =
                                archivosComentarios[comentario.id];
                              const link = document.createElement("a");
                              link.href = archivo.url;
                              link.download =
                                archivo.nombreDescarga || "archivo";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            <i
                              className="pi pi-download"
                              css={{ marginRight: "0.5em" }}
                            ></i>
                            {archivosComentarios[comentario.id]
                              .nombreDescarga || "Descargar archivo"}
                          </Boton>
                        )
                      ) : (
                        <Skeleton width="200px" height="150px" />
                      )}
                    </div>
                  )}
                  <div css={{ fontSize: "0.875em", color: "#666" }}>
                    <div>
                      Ingresado por{" "}
                      <strong>
                        {comentario.auditor?.nombre}{" "}
                        {comentario.auditor?.apellido}
                      </strong>
                      , en <strong>{formatFecha(comentario.fecha)}</strong>, en
                      la actividad de{" "}
                      <strong>
                        {comentario.actividadPaso?.nombre}
                      </strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p css={{ marginTop: "1em", color: "#666" }}>
              No hay comentarios para este campo
            </p>
          )}

          <p css={{ marginTop: "2em" }}>Histórico de validaciones:</p>
          {campoSeleccionado?.historico &&
          campoSeleccionado.historico.length > 0 ? (
            <div css={{ marginTop: "1em" }}>
              {[...campoSeleccionado.historico]
                .reverse()
                .map((validacion, index) => {
                  const tieneAprobacion =
                    validacion.aprobacion !== null &&
                    validacion.aprobacion !== undefined;
                  const backgroundColor = !tieneAprobacion
                    ? "#f5f5f5"
                    : validacion.aprobacion
                    ? "#f0f9f0"
                    : "#fff5f5";

                  return (
                    <div
                      key={index}
                      css={{
                        marginBottom: "1em",
                        padding: "1em",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: backgroundColor,
                      }}
                    >
                      <div
                        css={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "0.5em",
                        }}
                      >
                        {tieneAprobacion ? (
                          <>
                            <i
                              className={
                                validacion.aprobacion
                                  ? "pi pi-check"
                                  : "pi pi-times"
                              }
                              css={{
                                color: validacion.aprobacion
                                  ? "var(--verde)"
                                  : "var(--rojo)",
                                marginRight: "0.5em",
                              }}
                            ></i>
                            <strong>
                              {validacion.aprobacion ? "Aprobado" : "Rechazado"}
                            </strong>
                          </>
                        ) : (
                          <strong css={{ color: "#666" }}>Ingreso</strong>
                        )}
                      </div>
                      {validacion.valor && (
                        <p css={{ margin: "0.5em 0" }}>
                          <strong>Nuevo valor:</strong> {validacion.valor}
                        </p>
                      )}
                      {validacion.comentario && (
                        <p css={{ margin: "0.5em 0" }}>
                          {validacion.comentario}
                        </p>
                      )}
                      <div css={{ fontSize: "0.875em", color: "#666" }}>
                        <div>
                          Ingresado por{" "}
                          <strong>{validacion.colaborador}</strong>, en{" "}
                          <strong>{formatFecha(validacion.fecha)}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p css={{ marginTop: "1em", color: "#666" }}>
              No hay validaciones históricas para este campo
            </p>
          )}
        </div>
      </Sidebar>

      <TablaFiltrable conFiltros={false}
        value={camposEnProducto}
        emptyMessage="No hay archivos cargados"
        size="small"
        style={{ marginBottom: "2em" }}
      >
        <Column
          field={(campo) =>
            campo.productocampospropios.unidades
              ? `${campo.productocampospropios.etiqueta} (${campo.productocampospropios.unidades})`
              : campo.productocampospropios.etiqueta
          }
          header="Campo"
        ></Column>
        <Column field={(campo) => `${campo.valor}`} header="Valor"></Column>
        {hayCambiosPendientes && (
          <Column
            header="Cambio pendiente"
            body={(campo) => {
              const cambio = cambiosPorCampo[campo.id];
              if (!cambio) return null;
              return (
                <span
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.4em",
                    color: "var(--rojo)",
                    fontWeight: 600,
                  }}
                >
                  <i className="pi pi-exclamation-triangle"></i>
                  {cambio.valornuevo || "(vacío)"}
                </span>
              );
            }}
          ></Column>
        )}
        <Column
          field={(campo) => {
            if (!campo.ComentarioCampo || campo.ComentarioCampo.length === 0) {
              return null;
            }

            const todosAprobados = campo.ComentarioCampo.every(
              (comentario) => comentario.aprobado === true
            );
            const todosRechazados = campo.ComentarioCampo.every(
              (comentario) => comentario.aprobado === false
            );

            if (todosAprobados) {
              return (
                <i
                  className="pi pi-check"
                  css={{ color: "var(--verde)" }}
                ></i>
              );
            } else if (todosRechazados) {
              return (
                <i
                  className="pi pi-times"
                  css={{ color: "var(--rojo)" }}
                ></i>
              );
            } else {
              return (
                <i
                  className="pi pi-exclamation-circle"
                  css={{ color: "var(--amarillo)" }}
                ></i>
              );
            }
          }}
          style={{ width: "50px" }}
        ></Column>
        <Column
          body={(campo) => {
            const ultimaValidacion =
              campo.historico?.length > 0
                ? campo.historico[campo.historico.length - 1]
                : null;

            const mostrarIcono =
              ultimaValidacion &&
              ultimaValidacion.pasoId === pasoId &&
              ultimaValidacion.aprobacion !== null &&
              ultimaValidacion.aprobacion !== undefined;

            if (!mostrarIcono) return null;

            return (
              <i
                className={
                  ultimaValidacion.aprobacion
                    ? "pi pi-check"
                    : "pi pi-times"
                }
                css={{
                  fontWeight: "900",
                  color: ultimaValidacion.aprobacion
                    ? "var(--verde)"
                    : "var(--rojo)",
                }}
              />
            );
          }}
          style={{ width: "30px" }}
        />
        {!modoLectura && (
          <Column
            body={(campo) => (
              <Boton
                texto
                chico
                negro
                css={{ borderRadius: "0" }}
                callback={() =>
                  setCampoSeleccionado({
                    id: campo.id,
                    nombre: campo.productocampospropios.unidades
                      ? `${campo.productocampospropios.etiqueta} (${campo.productocampospropios.unidades})`
                      : campo.productocampospropios.etiqueta,
                    comentarios: campo.ComentarioCampo || [],
                    historico: campo.historico || [],
                    campo: campo,
                  })
                }
              >
                <i className="pi pi-chevron-right"></i>
              </Boton>
            )}
            style={{ width: "30px" }}
          />
        )}
      </TablaFiltrable>
    </>
  );
}
